import { HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import {
  DefaultOrgContext,
  DefaultStoreContext,
  EhNotification,
  EnvService,
  JwtService,
  LoggerService,
  ModalService,
  Organization,
  OrgStore,
  ScopeType,
  Session,
  User
} from '@enliven/angular';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  defer,
  find,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import { ORG_WEBSITE_BASE_URL } from '../constants/storage-constants';
import { CognitoService } from '../services/cognito.service';
import { DataService } from '../services/data.service';
import { DirectLoginService } from '../services/direct-login.service';
import { E360SessionService } from '../services/e360-session.service';
import { E360UserService } from '../services/e360-user.service';
import { StateService } from '../services/state.service';

export const isAuthorizedGuard: CanActivateFn = () => {
  const cognitoService = inject(CognitoService);
  const directLoginService = inject(DirectLoginService);
  const e360SessionService = inject(E360SessionService);
  const envService = inject(EnvService);
  const e360UserService = inject(E360UserService);
  const modalService = inject(ModalService);
  const dataService = inject(DataService);
  const logger = inject(LoggerService);
  const stateService = inject(StateService);
  const jwtService = inject(JwtService);

  let defaultStoreContext: DefaultStoreContext = e360SessionService.defaultStoreContext;
  let defaultOrgContext: DefaultOrgContext = e360SessionService.defaultOrganizationContext;

  const ssoSignOut = (message: string) => {
    cognitoService.signOut({ message });
  };

  const signOut = (uiMsg: string, logMsg: string, logMsgContext: object) => {
    logger.error(logMsg, logMsgContext);
    if (e360SessionService.isSSO) {
      ssoSignOut(uiMsg);
    } else {
      directLoginService.signOut(uiMsg);
    }
  };

  const getUsersLastSavedSession = (user: User): Observable<Session> => {
    return e360SessionService.getLastSavedSession(user).pipe(
      switchMap((lastSavedSession: Session) => {
        if (lastSavedSession) {
          return of(lastSavedSession);
        }
        return of(null);
      })
    );
  };

  const findStoreInCurrentOrg = (storeId: string, storeIdType: string, stores: OrgStore[]): OrgStore => {
    return stores.find(s => s[storeIdType] === storeId || s.organizationStoreId === storeId);
  };

  const findStoreForSelectedOrg = (stores: OrgStore[]): OrgStore => {
    return stores.find(s => s.organizationStoreId === defaultStoreContext.storeId);
  };

  const noDefaultStoreAccess = () => {
    logger.info(`User logging in with default store that they cannot access: ${e360UserService.user.id}`, {
      err: { defaultStore: defaultStoreContext }
    });
    modalService.notify('WARNINGS.NO_ACCESS_TO_DEFAULT_STORE', EhNotification.WARNING);
    // remove default store context (this will allow the state to set store context to the first item in the list)
    e360SessionService.setDefaultStore({ storeId: null, storeIdType: null });
  };

  // checking if a user can access the default org / store from the last session or URL logged in w/
  const canUserAccessDefaultStore = (user: User, storesForCurrentlySelectedOrg: OrgStore[]): Observable<boolean> => {
    const { storeId, storeIdType } = defaultStoreContext ?? {};
    const scope = user.scopeRoles[0].scope;

    if (!storeId || scope.type === ScopeType.GLOBAL) return of(true); // no store in URL or global user

    const defaultStore = storesForCurrentlySelectedOrg.find(
      s => s[storeIdType] === storeId || s.organizationStoreId === storeId
    );

    if (scope.type === ScopeType.PHARMACY_STORES) {
      if (!defaultStore) {
        noDefaultStoreAccess();
      }
      // return true for both instances since they do have access to a store in the org
      return of(true);
    }

    if (defaultStore) return of(true); // org/multiorg user where store was found in current store list.

    // The below stems from ADUSA, and is very much an edge case.
    // A multi org user where the default store in the URL is not found within the selected org's list of stores.
    // Try to find the default store by iterating over the other orgs in scope until a match is found or finished searching.
    const orgsToQuery: string[] = scope.value.organizations
      .filter(o => o.id !== e360SessionService.orgId)
      .map(o => o.id);

    return from(orgsToQuery).pipe(
      concatMap(orgId => {
        const params = new HttpParams({
          fromObject: {
            idType: storeIdType,
            search: storeId
          }
        });
        return dataService.getAllStoresForOrg(orgId, params).pipe(
          map((storesMatchingQuery: OrgStore[]) => {
            const matchedStore = findStoreInCurrentOrg(storeId, storeIdType, storesMatchingQuery);
            return { store: matchedStore, orgId };
          }),
          catchError(() => of({ store: undefined, orgId })) // Ignore errors and continue iterating
        );
      }),
      find(({ store }) => !!store), // Stop once a store is matched
      switchMap(result => {
        if (result?.store) {
          e360SessionService.orgId = result.orgId;
          stateService.selectedOrg = stateService.orgs.find(o => o.organizationId === result.orgId);
          return dataService.getAllStoresForOrg(result.orgId).pipe(
            map((storeListForUpdatedSelectedOrg: OrgStore[]) => {
              const storeContext = findStoreForSelectedOrg(storeListForUpdatedSelectedOrg);
              // set selected store to the default store context
              e360SessionService.setDefaultStore({
                storeId: storeContext?.organizationStoreId,
                storeIdType: 'organizationstoreid'
              });
              stateService.orgStores = storeListForUpdatedSelectedOrg;
              return true;
            }),
            catchError(() => {
              noDefaultStoreAccess();
              return of(true);
            })
          );
        }
        noDefaultStoreAccess();
        return of(true);
      }),
      defaultIfEmpty(false)
    );
  };

  // For both SSO and Direct Login, get user sub from stored idToken JWT
  // Direct Login now uses SSO-style token management after initial authentication
  const getUserSub = (): string | null => {
    try {
      const decodedToken: any = jwtService.decodeToken(e360SessionService.idToken);
      return decodedToken?.sub || null;
    } catch (error) {
      return null;
    }
  };

  const user$: Observable<User> = e360SessionService.isSSO
    ? of(e360UserService.user)
    : defer(() => {
        const userSub = getUserSub();
        return dataService.getUserByOrg(e360UserService.userOrgId, userSub);
      });

  const orgWebsiteBaseUrl = sessionStorage.getItem(ORG_WEBSITE_BASE_URL);
  if (!envService.isLocal && orgWebsiteBaseUrl !== window.location.origin) {
    signOut(
      'ALERT_PAGE.ACCESS_DENIED',
      `User attempting to access E360 via incorrect URL. Expected URL: ${orgWebsiteBaseUrl}. Received URL: ${window.location.origin}.`,
      e360SessionService.session
    );
    return false;
  }

  const fetchOrgs = (user: User): Observable<Organization[]> => {
    return dataService.getOrgsPage(1, 100).pipe(
      switchMap(({ orgs }) => {
        const orgIdToEnsure = e360SessionService.orgId;
        const orgInList = orgs.find(o => o.organizationId === orgIdToEnsure);
        if (!orgInList && orgIdToEnsure) {
          return dataService.getOrgFromOrgApi(orgIdToEnsure).pipe(
            map(currentOrg => [currentOrg, ...orgs]),
            catchError(() => of(orgs))
          );
        }
        return of(orgs);
      }),
      tap(orgs => (stateService.orgs = orgs))
    );
  };

  const resolveLastSession = (user: User, orgs: Organization[]): Observable<Session> => {
    const cognitoOrgId = e360SessionService.session.cognito.organizationId;
    const isSingleOrgLinkedUser =
      !user.modules.every(m => m.organizationId === orgs[0].organizationId) &&
      !e360UserService.isMultiOrgUser &&
      !e360UserService.isGlobalUser;
    const linkedUserOrgsMatch = cognitoOrgId === user.organizationId;

    if (isSingleOrgLinkedUser && cognitoOrgId && !linkedUserOrgsMatch) {
      return throwError(() => ({
        signOutText: 'ALERT_PAGE.INCORRECT_ORGANIZATION',
        errorMessage: `User organization ID: ${user.organizationId} for ${user.userName} does not match expected cognito org id value ${cognitoOrgId}. Possible direct login with wrong organization param.`
      }));
    }

    if (!defaultStoreContext && user.scopeRoles?.[0].scope?.type !== ScopeType.GLOBAL) {
      return getUsersLastSavedSession(user).pipe(
        tap((sessionInfo: Session) => {
          const defaultUserOrg = orgs.find(
            o => o.organizationId === sessionInfo?.defaultOrganizationContext?.organizationId
          );
          if (defaultUserOrg) {
            stateService.selectedOrg = defaultUserOrg;
            defaultOrgContext = sessionInfo.defaultOrganizationContext;
            e360SessionService.setDefaultOrg(defaultOrgContext);
            // only set default store context from last saved session if the user can access the last saved org
            defaultStoreContext = sessionInfo?.defaultStoreContext
              ? sessionInfo.defaultStoreContext
              : defaultStoreContext;
          } else {
            defaultOrgContext = {
              organizationId: user.organizationId,
              organizationIdType: 'organizationId'
            };
            e360SessionService.setDefaultOrg(defaultOrgContext);
            // IP-13809 - request to add warning if org changed from last saved session
            modalService.notify('WARNINGS.NO_ACCESS_TO_DEFAULT_ORG', EhNotification.WARNING);
          }
        })
      );
    }
    return of(null);
  };

  const resolveOrgId = (lastSavedSession: Session): string => {
    return e360SessionService.orgId;
  };

  const applyDefaultStoreAndCheckAccess = (user: User, stores: OrgStore[]): Observable<boolean> => {
    if (defaultStoreContext?.storeId) {
      const storeContext = stores.find(s => s.organizationStoreId === defaultStoreContext.storeId);
      e360SessionService.setDefaultStore({
        storeId: storeContext?.organizationStoreId,
        storeIdType: 'organizationstoreid'
      });
    }
    stateService.orgStores = stores;
    return canUserAccessDefaultStore(user, stores).pipe(
      map(canAccess => {
        if (!canAccess) {
          signOut(
            'ALERT_PAGE.NO_ACCESS_TO_STORE',
            `User logging in with default store that they cannot access: ${user.id}`,
            { err: { defaultStore: defaultStoreContext } }
          );
        }
        return canAccess;
      })
    );
  };

  return user$.pipe(
    switchMap((user: User) => {
      return fetchOrgs(user).pipe(
        switchMap(orgs => resolveLastSession(user, orgs)),
        map(lastSavedSession => resolveOrgId(lastSavedSession)),
        switchMap(orgId => dataService.getAllStoresForOrg(orgId)),
        switchMap(stores => applyDefaultStoreAndCheckAccess(user, stores))
      );
    }),
    catchError(err => {
      if (err.signOutText === 'ALERT_PAGE.INCORRECT_ORGANIZATION') {
        signOut(err.signOutText, err.errorMessage, { err });
      } else {
        signOut('ALERT_PAGE.PROBLEM_GATHERING_DATA', `Error while gathering data for user.`, { err });
      }
      return of(false);
    })
  );
};
